import React from "react"
import "../PrivacyPolicy.css"
import LanguageSwitcher from "../components/LanguageSwitcher"
import { useLanguage } from "../i18n/LanguageContext"

const englishSections = [
  {
    title: "1. Information We Collect",
    body: (
      <>
        <p>Depending on how you use Tout&apos;s Routes, we may collect information that you provide directly, including your name, email address, phone number, account credentials, and messages sent to support.</p>
        <p>When you use transportation, booking, route, or live-tracking features, we may process trip details, pickup and destination information, booking status, captain or driver information, and location information when you enable a location-based feature or grant permission on your device.</p>
        <p>We may also receive technical information such as device type, operating system, application version, IP address, browser information, diagnostic data, and security or event logs.</p>
      </>
    ),
  },
  {
    title: "2. How We Use Information",
    body: (
      <p>We use information to provide, maintain, secure, and improve Tout&apos;s Routes; authenticate accounts; manage bookings and routes; support live transportation features; communicate with you; respond to support requests; prevent fraud and misuse; troubleshoot technical problems; comply with legal obligations; and protect the rights, safety, and security of our users and services.</p>
    ),
  },
  {
    title: "3. Location Information",
    body: (
      <p>Some transportation and live-tracking features may require location information. Location access is requested through your device permissions and can be controlled in your device settings. We use location information only when needed to provide the feature you choose, to support trip operations, or for related safety and service functions. You may disable location access, but features that depend on it may not work correctly.</p>
    ),
  },
  {
    title: "4. How We Share Information",
    body: (
      <p>We may share information with service providers that help us host, secure, operate, support, and communicate about the service; with transportation partners or authorized users when needed to provide a booking or route feature; with professional advisers; or when required by law, legal process, or a valid request from a government authority. We do not sell your personal information.</p>
    ),
  },
  {
    title: "5. Data Retention and Security",
    body: (
      <p>We retain information for as long as reasonably necessary to provide the service, maintain business and security records, resolve disputes, enforce agreements, and comply with legal obligations. We use reasonable administrative, technical, and organizational safeguards designed to protect information. No internet transmission or storage system can be guaranteed to be completely secure.</p>
    ),
  },
  {
    title: "6. Your Choices and Rights",
    body: (
      <p>You may update certain account details, manage device permissions, and request access to, correction of, or deletion of personal information where applicable. You may also contact us with questions or privacy requests. We may need to verify your identity before completing a request, and some information may need to be retained when required by law or for legitimate operational purposes.</p>
    ),
  },
  {
    title: "7. Children's Privacy",
    body: (
      <p>Tout&apos;s Routes is not directed to children under 13, and we do not knowingly collect personal information from children under 13. If you believe a child has provided personal information, please contact us so that we can take appropriate action.</p>
    ),
  },
  {
    title: "8. Third-Party Services and Links",
    body: (
      <p>The service may rely on third-party infrastructure or contain links to third-party services. Their privacy practices are governed by their own policies, not this Privacy Policy. We encourage you to review those policies before using third-party services.</p>
    ),
  },
  {
    title: "9. Changes to This Policy",
    body: (
      <p>We may update this Privacy Policy when the service, legal requirements, or our practices change. The updated version will be posted at this public URL with a revised effective date. Your continued use of the service after an update means that you acknowledge the updated policy.</p>
    ),
  },
  {
    title: "10. Contact Us",
    body: (
      <p>If you have questions, requests, or concerns about privacy, contact Tout&apos;s Routes at <a href="mailto:toutsroutes@gmail.com">toutsroutes@gmail.com</a>.</p>
    ),
  },
]

const arabicSections = [
  {
    title: "1. المعلومات التي نجمعها",
    body: (
      <>
        <p>بحسب طريقة استخدامك لتطبيق وموقع Tout&apos;s Routes، قد نجمع المعلومات التي تقدمها مباشرة، مثل الاسم وعنوان البريد الإلكتروني ورقم الهاتف وبيانات الدخول والرسائل المرسلة إلى الدعم.</p>
        <p>عند استخدام ميزات النقل أو الحجز أو المسارات أو التتبع المباشر، قد نعالج تفاصيل الرحلة ومعلومات نقطة الالتقاء والوجهة وحالة الحجز ومعلومات القبطان أو السائق ومعلومات الموقع عند تفعيل ميزة تعتمد على الموقع أو منح الإذن من جهازك.</p>
        <p>قد نجمع أيضاً معلومات تقنية مثل نوع الجهاز ونظام التشغيل وإصدار التطبيق وعنوان IP ومعلومات المتصفح وبيانات التشخيص وسجلات الأمان أو الأحداث.</p>
      </>
    ),
  },
  {
    title: "2. كيفية استخدام المعلومات",
    body: (
      <p>نستخدم المعلومات لتقديم Tout&apos;s Routes وتشغيله وتأمينه وتحسينه، والتحقق من الحسابات، وإدارة الحجوزات والمسارات، ودعم ميزات النقل المباشر، والتواصل معك، والرد على طلبات الدعم، ومنع الاحتيال وإساءة الاستخدام، ومعالجة المشكلات التقنية، والامتثال للالتزامات القانونية، وحماية حقوق مستخدمينا وسلامتهم وأمن خدماتنا.</p>
    ),
  },
  {
    title: "3. معلومات الموقع",
    body: (
      <p>قد تتطلب بعض ميزات النقل والتتبع المباشر معلومات الموقع. يتم طلب الوصول إلى الموقع من خلال أذونات جهازك، ويمكن التحكم فيه من إعدادات الجهاز. نستخدم معلومات الموقع فقط عند الحاجة إلى تقديم الميزة التي تختارها أو دعم عمليات الرحلة أو وظائف السلامة والخدمة المرتبطة بها. يمكنك إيقاف الوصول إلى الموقع، ولكن قد لا تعمل الميزات التي تعتمد عليه بشكل صحيح.</p>
    ),
  },
  {
    title: "4. كيفية مشاركة المعلومات",
    body: (
      <p>قد نشارك المعلومات مع مزودي الخدمات الذين يساعدوننا في استضافة الخدمة وتأمينها وتشغيلها ودعمها والتواصل بشأنها، ومع شركاء النقل أو المستخدمين المصرح لهم عند الحاجة لتقديم ميزة الحجز أو المسار، ومع المستشارين المهنيين، أو عندما يقتضي القانون أو إجراء قانوني أو طلب صحيح من جهة حكومية ذلك. نحن لا نبيع معلوماتك الشخصية.</p>
    ),
  },
  {
    title: "5. الاحتفاظ بالبيانات وأمنها",
    body: (
      <p>نحتفظ بالمعلومات للمدة اللازمة بشكل معقول لتقديم الخدمة والاحتفاظ بسجلات العمل والأمن وحل النزاعات وتنفيذ الاتفاقيات والامتثال للالتزامات القانونية. نستخدم وسائل حماية إدارية وتقنية وتنظيمية معقولة مصممة لحماية المعلومات. ولا يمكن ضمان أمان أي نقل أو تخزين عبر الإنترنت بشكل كامل.</p>
    ),
  },
  {
    title: "6. اختياراتك وحقوقك",
    body: (
      <p>يمكنك تحديث بعض بيانات الحساب وإدارة أذونات الجهاز وطلب الوصول إلى المعلومات الشخصية أو تصحيحها أو حذفها حيثما ينطبق ذلك. كما يمكنك التواصل معنا بشأن الأسئلة أو الطلبات المتعلقة بالخصوصية. قد نحتاج إلى التحقق من هويتك قبل تنفيذ الطلب، وقد نحتفظ ببعض المعلومات عندما يقتضي القانون ذلك أو لأغراض تشغيلية مشروعة.</p>
    ),
  },
  {
    title: "7. خصوصية الأطفال",
    body: (
      <p>لا يستهدف Tout&apos;s Routes الأطفال الذين تقل أعمارهم عن 13 عاماً، ولا نجمع عن علم معلومات شخصية من الأطفال دون 13 عاماً. إذا كنت تعتقد أن طفلاً قدم معلومات شخصية، يرجى التواصل معنا حتى نتخذ الإجراء المناسب.</p>
    ),
  },
  {
    title: "8. خدمات وروابط الجهات الخارجية",
    body: (
      <p>قد تعتمد الخدمة على بنية تحتية تابعة لجهات خارجية أو تتضمن روابط إلى خدمات خارجية. تخضع ممارسات الخصوصية الخاصة بهذه الجهات لسياساتها الخاصة وليس لسياسة الخصوصية هذه. ننصحك بمراجعة تلك السياسات قبل استخدام خدمات الجهات الخارجية.</p>
    ),
  },
  {
    title: "9. التغييرات على هذه السياسة",
    body: (
      <p>قد نحدّث سياسة الخصوصية هذه عند تغير الخدمة أو المتطلبات القانونية أو ممارساتنا. سيتم نشر النسخة المحدثة على عنوان URL العام هذا مع تعديل تاريخ السريان. استمرارك في استخدام الخدمة بعد التحديث يعني اطلاعك على السياسة المحدثة.</p>
    ),
  },
  {
    title: "10. التواصل معنا",
    body: (
      <p>إذا كانت لديك أسئلة أو طلبات أو مخاوف تتعلق بالخصوصية، يمكنك التواصل مع Tout&apos;s Routes عبر البريد الإلكتروني <a href="mailto:toutsroutes@gmail.com">toutsroutes@gmail.com</a>.</p>
    ),
  },
]

function PolicySection({ section }) {
  return (
    <section className="policy-section">
      <h3>{section.title}</h3>
      {section.body}
    </section>
  )
}

function PrivacyPolicy() {
  const { language, isRTL, t } = useLanguage()
  const sections = isRTL ? arabicSections : englishSections

  return (
    <main className="policy-page" lang={language} dir={isRTL ? "rtl" : "ltr"}>
      <div className="policy-shell">
        <header className="policy-header">
          <div className="policy-toolbar">
            <a className="policy-back-link" href="/login">{isRTL ? "→" : "←"} {t("privacy.backToSignIn")}</a>
            <LanguageSwitcher compact />
          </div>
          <div className="policy-brand">Tout&apos;s Routes</div>
          <h1>{t("privacy.title")}</h1>
          <p className="policy-meta">{t("privacy.effectiveDate")}</p>
          <p className="policy-intro">{t("privacy.intro")}</p>
        </header>

        <article className={`policy-card ${isRTL ? "policy-card-arabic" : ""}`} lang={language} dir={isRTL ? "rtl" : "ltr"}>
          {sections.map((section) => <PolicySection key={section.title} section={section} />)}
        </article>

        <footer className="policy-footer">
          <a href="/login">{t("privacy.signIn")}</a>
          <span aria-hidden="true"> · </span>
          <a href="mailto:toutsroutes@gmail.com">{t("privacy.contactSupport")}</a>
        </footer>
      </div>
    </main>
  )
}

export default PrivacyPolicy
